import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { KeyRound, Loader2, RotateCcw, Trash2, UserX } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { MonoValue } from '@/components/DataDisplay'
import { DangerDialog } from '@/components/DangerDialog'
import { useToast } from '@/providers/UiProviders'
import { CardBackLink, Body, ActionCard, ReasonInput } from './userActionKit'
import {
  USERS_STR as S,
  deleteAccount,
  restoreAccount,
  impersonateUser,
  isPending,
} from './usersAdminApi'

/**
 * A3.34 — Akkauntni o'chirish (ma'muriy, soft-delete → tiklanadi). Destruktiv →
 * DangerDialog L2 (sabab majburiy, auditlanadi). Greenfield POST
 * (`/admin/users/:id/delete`) → 404 bo'lsa «kutilmoqda».
 */
export function AccountDelete() {
  const { id = '' } = useParams()
  const userId = Number(id)
  const { toast } = useToast()
  const [open, setOpen] = useState(false)

  return (
    <>
      <PageHeader title={S.delTitle} screenId="A3.34" actions={<CardBackLink id={id} />} />
      <Body>
        <ActionCard hint={S.delHint}>
          <button
            onClick={() => setOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-md border border-danger bg-danger-subtle px-4 py-1.5 text-sm text-danger hover:bg-danger hover:text-white"
          >
            <Trash2 size={14} />
            {S.delBtn}
          </button>
        </ActionCard>
      </Body>

      {open ? (
        <DangerDialog
          open
          onClose={() => setOpen(false)}
          screenId="A3.34"
          level={2}
          title={`${S.delBtn} · #${userId}`}
          description={S.delHint}
          confirmLabel={S.delBtn}
          onConfirm={async ({ reason }) => {
            try {
              await deleteAccount(userId, reason)
              toast(S.done, { tone: 'success' })
            } catch (e) {
              if (isPending(e)) {
                toast(S.pending, { tone: 'warning' })
                return
              }
              toast(S.failed, { tone: 'error', ...(e instanceof Error && e.message ? { description: e.message } : {}) })
              throw e
            }
          }}
        />
      ) : null}
    </>
  )
}

/**
 * A3.35 — Akkauntni tiklash. Oldin o'chirilgan akkauntni qaytarish. Greenfield
 * POST (`/admin/users/:id/restore`) → 404 bo'lsa «kutilmoqda».
 */
export function AccountRestore() {
  const { id = '' } = useParams()
  const userId = Number(id)
  const { toast } = useToast()
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)

  const submit = async () => {
    if (reason.trim().length < 3 || busy) return
    setBusy(true)
    try {
      await restoreAccount(userId, reason.trim())
      setReason('')
      toast(S.done, { tone: 'success' })
    } catch (e) {
      if (isPending(e)) toast(S.pending, { tone: 'warning' })
      else toast(S.failed, { tone: 'error', ...(e instanceof Error && e.message ? { description: e.message } : {}) })
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <PageHeader title={S.restoreTitle} screenId="A3.35" actions={<CardBackLink id={id} />} />
      <Body>
        <ActionCard hint={S.restoreHint}>
          <ReasonInput value={reason} onChange={setReason} />
          <button
            onClick={() => void submit()}
            disabled={busy || reason.trim().length < 3}
            className="inline-flex items-center gap-1.5 rounded-md bg-accent px-4 py-1.5 text-sm font-medium text-white hover:bg-accent-hover disabled:opacity-50"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <RotateCcw size={14} />}
            {S.restoreBtn}
          </button>
        </ActionCard>
      </Body>
    </>
  )
}

/**
 * A3.37 — Impersonation (debug uchun, FAQAT test muhiti). Foydalanuvchi sifatida
 * vaqtinchalik token olish. Prod serverда RAD etiladi (xavfsizlik chegarasi
 * serverда). Greenfield POST → 404 bo'lsa «kutilmoqda».
 */
export function Impersonation() {
  const { id = '' } = useParams()
  const userId = Number(id)
  const { toast } = useToast()
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [token, setToken] = useState<string | null>(null)

  const submit = async () => {
    if (reason.trim().length < 3 || busy) return
    setBusy(true)
    setToken(null)
    try {
      const res = await impersonateUser(userId, reason.trim())
      setToken(res.token ?? null)
      toast(S.done, { tone: 'success' })
    } catch (e) {
      if (isPending(e)) toast(S.pending, { tone: 'warning' })
      else toast(S.failed, { tone: 'error', ...(e instanceof Error && e.message ? { description: e.message } : {}) })
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <PageHeader title={S.impTitle} screenId="A3.37" actions={<CardBackLink id={id} />} />
      <Body>
        <ActionCard hint={S.impHint}>
          <div className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-300">
            <UserX size={14} className="mt-0.5 shrink-0" />
            <span>FAQAT test muhiti. Prod serverда bu so'rov rad etiladi.</span>
          </div>
          <ReasonInput value={reason} onChange={setReason} />
          <button
            onClick={() => void submit()}
            disabled={busy || reason.trim().length < 3}
            className="inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-1 px-4 py-1.5 text-sm text-secondary hover:text-primary disabled:opacity-50"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <KeyRound size={14} />}
            {S.impBtn}
          </button>
          {token ? (
            <div>
              <div className="mb-1 text-xs text-tertiary">{S.impToken}</div>
              <MonoValue value={token} />
            </div>
          ) : null}
        </ActionCard>
      </Body>
    </>
  )
}
